import React, { useMemo } from 'react';
import Field from '../../Field';
import { READING_WORDING } from '../../../domain/reading';
import PhoneField from '../../PhoneField';
import { Radio } from '../../FormControls';
import { timeZoneOptions } from '../../../lib/intlOptions';
import { FAMILY_CONTACT_TEXT_MAX } from '../../../domain/people';
import s from './addStudent.module.css';

// Step 2: a student is always attached to a family, the billing entity, and
// the two are created together in one batch, so a student never exists with
// nobody to invoice.
//
// ── Time Zone is here, where MMS puts it ───────────────────────────────────
//
// With Multi-Time Zone Selection on, MMS's wizard offers Time Zone in its
// Family Details step, under Additional Details, as the last field before
// Parent Lesson Reminders; the student step has none. The sentence under it:
//
//     This time zone will also apply to all contacts and Students in this
//     student's Family.
//
// Two knowing differences. MMS has no "same as the studio" option — its
// dropdown is always a concrete zone, pre-filled with the studio's — where we
// keep null as a real value, because a stored copy of a setting goes stale
// when the setting changes. And we list IANA identifiers rather than MMS's
// Windows display names (`timeZoneOptions` in lib/intlOptions.js says why).

// ── An adult's new family asks for nothing but the zone ────────────────────
//
// With Student Type Adult, MMS's Family Details keeps both radios and, for A
// new family, drops Parent First Name, Parent Last Name, Email and Mobile
// Phone: the account is made from the student, named after them, with them as
// its contact and invoice recipient. `asksForParent` is that rule, the core's
// (`core/src/add_student.rs`, whose header has where we knowingly differ).

export default function FamilyStep({
  form, set, families, familiesRead = true, multiTimeZone = false, emailError = null, asksForParent = true,
}) {
  // An unread list of families is not "No family accounts yet" (#629): the
  // existing-family choice waits, disabled and unexplained, for the answer.
  const noFamiliesYet = families.length === 0;
  const familiesUnread = noFamiliesYet && !familiesRead;
  // Four hundred-odd options, derived from Intl once and cached in the module,
  // memoised so the list is not rebuilt on every keystroke.
  const zones = useMemo(() => (multiTimeZone ? timeZoneOptions() : []), [multiTimeZone]);

  return (
    <>
      <div className={s.groupLabel}>Add this student to:</div>
      <Radio
        name="fam" label="A new family"
        help="Creates a new account in Families & Invoices"
        checked={form.familyChoice === 'new'}
        onChange={() => set('familyChoice', 'new')}
      />
      <Radio
        name="fam" label="An existing family"
        help={familiesUnread
          ? READING_WORDING.loading
          : noFamiliesYet
            ? READING_WORDING['addStudent.noFamilies']
            : 'Pick from an existing account in Families & Invoices'}
        disabled={noFamiliesYet}
        checked={form.familyChoice === 'existing'}
        onChange={() => set('familyChoice', 'existing')}
      />

      {form.familyChoice === 'existing' ? (
        <div className={[s.spaced, s.medium].join(' ')}>
          <Field label="Family">
            <select
              className={s.input}
              value={form.existingFamilyId}
              onChange={(e) => set('existingFamilyId', e.target.value)}
            >
              <option value="">Select…</option>
              {families.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </Field>
        </div>
      ) : (
        <>
          {asksForParent ? (<>
          <div className={s.pair}>
            {/* Bounded by the rule that stores them (#120): `validFamilyContact`
                (#319), because these go into a `familyContacts` record and the
                family keeps only its own name.

                `parentLastName` does double duty: the contact's surname and,
                where the student's own is not used instead, the family's
                `lastName`, which an invoice is addressed to and the families
                list sorts by. The two ceilings are the same 199, so nothing is
                truncated. */}
            <Field label="Parent First Name">
              <input
                className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.firstName}
                value={form.parentFirstName}
                onChange={(e) => set('parentFirstName', e.target.value)}
              />
            </Field>
            <Field label="Parent Last Name">
              <input
                className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.lastName}
                value={form.parentLastName}
                onChange={(e) => set('parentLastName', e.target.value)}
              />
            </Field>
          </div>
          <div className={s.pair}>
            {/* Same check as the student's (#120): every invoice and reminder
                for this account is sent to this address. */}
            <Field label="Email" hint="Optional" error={emailError}>
              <input
                type="email" className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.email}
                value={form.parentEmail}
                onChange={(e) => set('parentEmail', e.target.value)}
              />
            </Field>
            {/* As on the student step, the tick qualifies this number: consent
                to text this parent, collected once, at intake. `addressOn`
                reads it as part of the SMS address, so an unticked number is not
                an address. */}
            <div className={s.pairCol}>
              {/* Same field and validation (src/domain/phone.js, #90): an
                  invoice reminder is texted to this number. */}
              <PhoneField
                id="family-phone"
                label="Phone"
                value={form.parentPhone}
                onChange={(v) => set('parentPhone', v)}
              />
              <label className={s.checkRow}>
                <input
                  type="checkbox" checked={form.parentSms}
                  onChange={(e) => set('parentSms', e.target.checked)}
                />
                <span>SMS Capable</span>
              </label>
            </div>
          </div>
          </>) : null}

          {/* Only when the studio has turned on Multi-Time Zone Selection
              (Business Settings → General → Event Scheduling), as MMS gates it:
              with the box unticked there is no zone field on any of its forms.
              Most studios teach everybody in one zone.

              Nothing here moves a lesson: a lesson is anchored in the zone its
              series was created in, as in MMS. What this decides is what the
              reminder and the cancellation notice tell this family
              (src/domain/timeZone.js, #47). */}
          {multiTimeZone ? (
            <div className={[s.spaced, s.medium].join(' ')}>
              <Field label="Time Zone" hint="Applies to everyone on this family account">
                <select
                  className={s.input}
                  value={form.familyTimeZone}
                  onChange={(e) => set('familyTimeZone', e.target.value)}
                >
                  <option value="">Same as the studio</option>
                  {zones.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                </select>
              </Field>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
